import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import VuiBox from "components/VuiBox";
import { Card, Grid } from "@mui/material";
import { useApi } from "hooks/useAPI";
import { CompareService, GoogleAggregate, GoogleMetric, Region } from "services/CompareService";
import { getLocalTime } from "utils/getLocalTime";
import { GOOGLE_METRIC_LABELS, getMetricOptions, getRegionOptions } from "./types/SelectTypes";
import { getGoogleChartOptions } from "./config/ChartConfig";
import { generateGoogleChartSeries } from "./utils/DataProcessingUtils";
import CompareChart from "./components/CompareChart";
import MetricSelector from "./components/MetricSelector";
import RegionSelector from "./components/RegionSelector";


const GoogleCompareChart = () => {
  const { get } = useApi();
  const selectedMerchant = useSelector((state: any) => state.merchants.selectedMerchant);
  const { startDate, endDate } = useSelector((state: any) => state.common);
  const [loading, setLoading] = useState(false);
  const [merchantData, setMerchantData] = useState<GoogleAggregate[]>([]);
  const [regionalData, setRegionalData] = useState<GoogleAggregate[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<GoogleMetric>("spend");
  const [selectedRegion, setSelectedRegion] = useState<Region | undefined>(undefined);

  const compareService = useMemo(() => new CompareService(get), []);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedMerchant?.id || !startDate || !endDate) return;

      setLoading(true);
      try {
        const { startDateString, endDateString } = getLocalTime(startDate, endDate);
        const result = await compareService.getGoogleCompareChart({
          merchantId: selectedMerchant.id,
          startDate: startDateString,
          endDate: endDateString,
          aggregateLevel: "daily",
          region: selectedRegion
        });

        setMerchantData(result.data.merchant);
        setRegionalData(result.data.benchmark);
      } catch (error) {
        console.error("Error fetching Google data:", error);
        setMerchantData([]);
        setRegionalData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedMerchant?.id, startDate, endDate, compareService, selectedRegion]);

  const series = useMemo(() => generateGoogleChartSeries(merchantData, regionalData, selectedMetric, selectedRegion), [merchantData, regionalData, selectedMetric, selectedRegion]);

  const options = useMemo(() => getGoogleChartOptions(selectedMetric), [selectedMetric]);

  // Handle region selection
  const handleRegionChange = (selectedOption: any) => {
    setSelectedRegion(selectedOption && selectedOption.value ? selectedOption.value as Region : undefined);
  };

  // Handle metric selection
  const handleMetricChange = (option: any) => {
    setSelectedMetric(option.value);
  };

  return (
    <Card>
      <VuiBox p={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MetricSelector
              title="Google Ads Performance"
              selectedMetric={selectedMetric}
              metricOptions={getMetricOptions(GOOGLE_METRIC_LABELS)}
              onMetricChange={handleMetricChange}
            />
          </Grid>
          <Grid item xs={12}>
            <RegionSelector
              regionOptions={getRegionOptions()}
              onRegionChange={handleRegionChange}
            />
          </Grid>
        </Grid>
        <CompareChart
          title="Google Ads Performance"
          options={options}
          series={series}
          loading={loading}
          noDataMessage="No Google data available for the selected period"
        />
      </VuiBox>
    </Card>
  );
};

export default GoogleCompareChart;
